import { Stack, Text } from '@fluentui/react';
import { NavGroup } from 'nav/NavGroup';
import { NavItem } from 'nav/NavGroup/NavItem';
import React, { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { expandedNav } from 'utility/cache/ui';
import { useStyles } from './index.styles';
import { SettingsOption } from './utils';

interface SettingsBaseProps {
  sections: SettingsOption[];
  basePath: string;
  title: string;
}

export const SettingsBase: React.FC<SettingsBaseProps> = ({
  sections,
  basePath,
  title,
}) => {
  const styles = useStyles();
  useEffect(() => {
    expandedNav(false);
  }, []);

  const renderMenu = () => {
    return (
      <Stack>
        {sections.map((item) =>
          item.children ? (
            <NavGroup
              key={item.name}
              data={{
                name: item.name,
                Icon: item.icon ?? 'Page',
                path: item.path,
                items: item.children.map((c) => ({
                  name: c.name,
                  path: c.path!,
                  Icon: c.icon ?? 'Page',
                })),
              }}
              expanded={true}
            />
          ) : (
            <NavItem
              key={item.name}
              navItemProps={{
                name: item.name,
                path: item.path!,
                Icon: item.icon ?? 'Page',
              }}
              text={item.name}
              to={item.path!}
              expanded={true}
              bold={false}
              Icon={item.icon ?? 'Page'}
            />
          )
        )}
      </Stack>
    );
  };

  const getAllPaths = (items: SettingsOption[]): string[] => {
    return items.flatMap((item) =>
      item.children ? getAllPaths(item.children) : item.path ? [item.path] : []
    );
  };

  const getFirstValidPath = (items: SettingsOption[]): string | undefined => {
    for (const item of items) {
      if (item.path) return item.path;
      if (item.children) {
        const childPath = getFirstValidPath(item.children);
        if (childPath) return childPath;
      }
    }
    return undefined;
  };
  const firstPath = getFirstValidPath(sections);
  const getAllRoutes = (items: SettingsOption[]): JSX.Element[] =>
    items.flatMap((item) => [
      ...(item.path && item.component
        ? [
            <Route
              key={item.path}
              path={item.path}
              component={item.component}
            />,
          ]
        : []),
      ...(item.children ? getAllRoutes(item.children) : []),
    ]);

  return (
    <Stack horizontal disableShrink className={styles.container}>
      <Stack className={styles.navTitle}>
        <Text variant="mediumPlus" className={styles.navHeader}>
          {title}
        </Text>
        <Stack className={styles.nav}>{renderMenu()}</Stack>
      </Stack>
      <Stack grow>
        <Switch>
          <Route
            path={basePath}
            exact
            render={() => (firstPath ? <Redirect to={firstPath} /> : null)}
          />

          {getAllRoutes(sections)}
        </Switch>
      </Stack>
    </Stack>
  );
};
